"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { renderRichText } from "@storyblok/react";
import { Button } from "@workspace/ui/components/button";
import { Card } from "@workspace/ui/components/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@workspace/ui/components/form";
import { Input } from "@workspace/ui/components/input";
import { Label } from "@workspace/ui/components/label";
import { Switch } from "@workspace/ui/components/switch";
import { Textarea } from "@workspace/ui/components/textarea";
import Link from "next/link";
import posthog from "posthog-js";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { type ContactFormValues, contactFormSchema } from "@/lib/schemas";
import type { StoryblokRichtext } from "@/types/storyblok";

export interface ContactFormBlok {
  component: "contactForm";
  _uid: string;
  lead?: StoryblokRichtext;
  [k: string]: unknown;
}

export default function ContactForm({ blok }: { blok?: ContactFormBlok }) {
  // Render lead richtext like Copy component
  const renderedContent = blok?.lead
    ? // @ts-expect-error - Storyblok richtext types are complex
      renderRichText(blok.lead)
    : null;
  const form = useForm<ContactFormValues>({
    resolver: zodResolver(contactFormSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      organisationName: "",
      phoneNumber: "",
      message: "",
      privacyPolicy: false,
      website: "",
    },
    mode: "onChange",
  });

  const onSubmit = async (data: ContactFormValues) => {
    try {
      const response = await fetch("/api/contact", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      });

      const result = await response.json();

      if (!response.ok) {
        const errorMessage = result.details
          ? `${result.error}: ${result.details}`
          : result.error || "Failed to send message";
        throw new Error(errorMessage);
      }

      // Identify user in PostHog with their contact info
      posthog.identify(data.email, {
        email: data.email,
        firstName: data.firstName,
        lastName: data.lastName,
        name: `${data.firstName} ${data.lastName}`,
        organisationName: data.organisationName || undefined,
        phoneNumber: data.phoneNumber || undefined,
      });

      // Capture successful form submission event
      posthog.capture("contact_form_submitted", {
        email: data.email,
        has_organisation: !!data.organisationName,
        has_phone: !!data.phoneNumber,
        message_length: data.message.length,
      });

      toast.success("Message sent successfully! We'll get back to you soon.");
      form.reset();
    } catch (error) {
      console.error("Form submission error:", error);

      // Capture form error event
      posthog.capture("contact_form_error", {
        error_message: error instanceof Error ? error.message : "Unknown error",
      });

      toast.error(
        error instanceof Error
          ? error.message
          : "Failed to send message. Please try again."
      );
    }
  };

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-12 space-y-6">
      {renderedContent && (
        <div
          className="typography max-w-none"
          // biome-ignore lint: Required for Storyblok richtext rendering
          dangerouslySetInnerHTML={{ __html: renderedContent }}
        />
      )}

      <Card className="p-6">
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <FormField
                control={form.control}
                name="firstName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>First Name</FormLabel>
                    <FormControl>
                      <Input placeholder="John" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="lastName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Last Name</FormLabel>
                    <FormControl>
                      <Input placeholder="Doe" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email</FormLabel>
                  <FormControl>
                    <Input
                      type="email"
                      placeholder="john.doe@example.com"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="organisationName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Organisation Name{" "}
                    <span className="text-muted-foreground">(Optional)</span>
                  </FormLabel>
                  <FormControl>
                    <Input placeholder="Acme Inc." {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="phoneNumber"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Phone Number{" "}
                    <span className="text-muted-foreground">(Optional)</span>
                  </FormLabel>
                  <FormControl>
                    <Input
                      type="tel"
                      placeholder="+1 (555) 123-4567"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="message"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Message</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Your message here..."
                      className="min-h-[120px]"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Honeypot field - hidden from users but visible to bots */}
            <div className="sr-only" aria-hidden="true">
              <FormField
                control={form.control}
                name="website"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Website</FormLabel>
                    <FormControl>
                      <Input
                        type="text"
                        tabIndex={-1}
                        autoComplete="off"
                        {...field}
                        value={field.value || ""}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
            </div>

            <div className="flex items-center gap-2">
              {/** biome-ignore lint/correctness/useUniqueElementIds: Unique element IDs are required for the switch */}
              <Switch
                id="privacy-policy"
                checked={form.watch("privacyPolicy")}
                onCheckedChange={(checked) =>
                  form.setValue("privacyPolicy", checked, {
                    shouldValidate: true,
                  })
                }
              />
              <Label htmlFor="privacy-policy">
                I agree to the
                <Link
                  href="/privacy"
                  className="text-teal-500 hover:text-teal-600 hover:underline duration-300"
                >
                  privacy policy
                </Link>
              </Label>
            </div>

            <Button
              type="submit"
              className="w-full"
              disabled={form.formState.isSubmitting || !form.formState.isValid}
            >
              {form.formState.isSubmitting ? "Submitting..." : "Submit"}
            </Button>
          </form>
        </Form>
      </Card>
    </div>
  );
}
